import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BadgeCheck, Timer, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import CheatSheetTab from "@/components/certification/CheatSheetTab";
import FlashcardDeck from "@/components/certification/FlashcardDeck";
import PracticeTestTab from "@/components/certification/PracticeTestTab";
import ExamHistoryTab from "@/components/certification/ExamHistoryTab";

export default function CertificationDetailPage() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [tab, setTab] = useState("cheatsheet");
  const [startingExam, setStartingExam] = useState(false);

  useEffect(() => {
    api.get(`/certifications/${certId}`).then((r) => setCert(r.data));
    setTab("cheatsheet");
  }, [certId]);

  const startExam = async () => {
    setStartingExam(true);
    try {
      const { data } = await api.post(`/certifications/${certId}/exam/start`);
      navigate(`/certifications/${certId}/exam/${data.attempt_id}`);
    } finally {
      setStartingExam(false);
    }
  };

  if (!cert) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="certification-detail-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="certification-detail-page">
      <div className="flex items-center gap-3 mb-2">
        <BadgeCheck className="w-6 h-6" style={{ color: cert.color }} />
        <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: cert.color }}>{cert.level}</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{cert.name}</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">{cert.description}</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="certification-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="cheatsheet" data-testid="cert-tab-cheatsheet">Cheat Sheet</TabsTrigger>
          <TabsTrigger value="flashcards" data-testid="cert-tab-flashcards">Flashcards</TabsTrigger>
          <TabsTrigger value="practice" data-testid="cert-tab-practice">Practice Test</TabsTrigger>
          <TabsTrigger value="exam" data-testid="cert-tab-exam">Mock Exam</TabsTrigger>
          <TabsTrigger value="history" data-testid="cert-tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="cheatsheet" className="mt-6"><CheatSheetTab cert={cert} /></TabsContent>
        <TabsContent value="flashcards" className="mt-6"><FlashcardDeck certId={certId} /></TabsContent>
        <TabsContent value="practice" className="mt-6"><PracticeTestTab certId={certId} /></TabsContent>
        <TabsContent value="exam" className="mt-6">
          <div className="glass-panel rounded-2xl p-10 text-center" data-testid="mock-exam-intro">
            <Timer className="w-8 h-8 text-indigo-400 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-white mb-2">Timed Mock Exam</h3>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">12 questions, 20 minutes. Flag questions to revisit, review all answers before submitting, and get a weak-area breakdown at the end.</p>
            <button onClick={startExam} disabled={startingExam} data-testid="start-mock-exam-btn" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              {startingExam ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Mock Exam"}
            </button>
          </div>
        </TabsContent>
        <TabsContent value="history" className="mt-6"><ExamHistoryTab certId={certId} /></TabsContent>
      </Tabs>
    </div>
  );
}
